'use client';

import React, { useCallback, useState } from 'react';
import { SnackbarProps, SxProps } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ErrorIcon from '@mui/icons-material/Dangerous';
import InfoIcon from '@mui/icons-material/CheckCircle';

import { useTheme, useToggle } from '@dh/hooks';

import { IconButton } from '../buttons';
import { CustomSnackbar } from './styles';
import { Column, Row } from '@dh/styles';

type SnackbarNotification = {
    message: React.ReactNode;
    contentPropsSx?: SxProps;
};

type PMPSnackbarProps = SnackbarProps &
    Partial<SnackbarNotification> & {
        isOpen: boolean;
        toggleIsOpen: (isOpen: boolean) => void;
        contentPropsSx?: SxProps;
    };

export const PMPSnackbar = ({
    isOpen,
    toggleIsOpen,
    contentPropsSx,
    message,
    ...snackbarProps
}: PMPSnackbarProps) => {
    const theme = useTheme();

    const handleClose = (e: React.SyntheticEvent | Event, reason?: string) => {
        if (reason === 'clickaway') {
            return;
        }
        toggleIsOpen(false);
    };

    const closeButtonColor = (contentPropsSx as any)?.color ?? theme.colors.onPrimary;
    const closeButton = (
        <IconButton
            icon={<CloseIcon sx={{ color: closeButtonColor }} />}
            aria-label='close'
            onClick={handleClose}
        />
    );

    return (
        <CustomSnackbar
            slotProps={{ content: { sx: contentPropsSx } }}
            open={isOpen}
            onClose={handleClose}
            action={closeButton}
            autoHideDuration={5000}
            onClick={(e) => e.stopPropagation()}
            message={message}
            {...snackbarProps}
        />
    );
};

export const useSnackbar = () => {
    const [isOpen, toggleIsOpen] = useToggle();
    const [notification, setNotification] = useState<SnackbarNotification>();
    const { colors } = useTheme();

    const showMessage = useCallback(
        (message: string) => {
            setNotification({ message });
            toggleIsOpen(true);
        },
        [toggleIsOpen]
    );

    const showError = useCallback(
        (message: string, messages?: string[]) => {
            setNotification({
                message: (
                    <Row gap={1}>
                        <ErrorIcon color='error' />
                        <Column>
                            {message}
                            {messages &&
                                messages.map((line, index) => (
                                    <span key={index}>
                                        {'- '}
                                        {line}
                                    </span>
                                ))}
                        </Column>
                    </Row>
                ),
                contentPropsSx: {
                    backgroundColor: colors.errorContainer,
                    border: `2px solid ${colors.error}`,
                    color: colors.onErrorContainer
                }
            });
            toggleIsOpen(true);
        },
        [colors.error, colors.errorContainer, colors.onErrorContainer, toggleIsOpen]
    );

    const showInfo = useCallback(
        (message: string) => {
            setNotification({
                message: (
                    <Row gap={1}>
                        <InfoIcon color='secondary' />
                        {message}
                    </Row>
                ),
                contentPropsSx: {
                    backgroundColor: colors.secondaryContainer,
                    border: `2px solid ${colors.secondary}`,
                    color: colors.secondary
                }
            });
            toggleIsOpen(true);
        },
        [colors.secondary, colors.secondaryContainer, toggleIsOpen]
    );

    return {
        props: {
            isOpen,
            toggleIsOpen,
            ...notification
        },
        showMessage,
        showError,
        showInfo
    };
};
